import React, { Component } from 'react';
import RedactedChip from '../../../../components/redacted-chip';
import { connect } from 'react-redux';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import MenuItem from '@material-ui/core/MenuItem';
import { loadSchemaAuditReport, downloadReport } from '../../data-dictionary-view.actions';
import ReportShell from './report-shell';

const DATE_RANGE_OPTIONS = [
  { label: 'Last 7 days', value: '7d' },
  { label: 'Last 30 days', value: '30d' },
  { label: 'Last 90 days', value: '90d' },
];

const CHANGE_TYPE_OPTIONS = ['ADD', 'DROP', 'ALTER'];

class SchemaAuditReport extends Component {
  constructor(props) {
    super(props);
    this.state = {
      dateRange: '30d',
      changeType: '',
      schema: '',
      changedBy: '',
      search: '',
    };
  }

  componentDidMount() {
    this.loadReport();
  }

  loadReport = () => {
    const { dateRange, changeType, schema, changedBy, search } = this.state;
    this.props.loadSchemaAuditReport({ dateRange, changeType, schema, changedBy, search });
  }

  handleFilterChange = (field) => (e) => {
    this.setState({ [field]: e.target.value }, this.loadReport);
  }

  handleDownload = () => {
    const { dateRange, changeType, schema, changedBy, search } = this.state;
    this.props.downloadReport('schema-audit', { dateRange, changeType, schema, changedBy, search });
  }

  handleCardClick = (key) => {
    const current = this.state.changeType;
    const next = (key === 'total' || current === key) ? '' : key;
    this.setState({ changeType: next }, this.loadReport);
  }

  getSummaryTiles(data) {
    const counts = data.reduce((acc, r) => {
      const type = (r.changeType || '').toUpperCase();
      if (type === 'ADD') acc.added++;
      else if (type === 'DROP') acc.dropped++;
      else if (type === 'ALTER') acc.altered++;
      return acc;
    }, { added: 0, dropped: 0, altered: 0 });
    const { changeType } = this.state;
    return [
      { label: 'Total', value: data.length, className: 'total', key: 'total', isActive: !changeType, onClick: () => this.handleCardClick('total') },
      { label: 'Added', value: counts.added, className: 'approved', key: 'ADD', isActive: changeType === 'ADD', onClick: () => this.handleCardClick('ADD') },
      { label: 'Dropped', value: counts.dropped, className: 'rejected', key: 'DROP', isActive: changeType === 'DROP', onClick: () => this.handleCardClick('DROP') },
      { label: 'Altered', value: counts.altered, className: 'pending', key: 'ALTER', isActive: changeType === 'ALTER', onClick: () => this.handleCardClick('ALTER') },
    ];
  }

  renderFilters() {
    const { dateRange, changeType, schema, changedBy, search } = this.state;
    const { schemas } = this.props;
    return (
      <div className="dd-view-filter-row">
        <TextField
          select
          label="Date Range"
          value={dateRange}
          onChange={this.handleFilterChange('dateRange')}
          variant="standard"
          fullWidth
        >
          {DATE_RANGE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
        <TextField
          select
          label="Change Type"
          value={changeType}
          onChange={this.handleFilterChange('changeType')}
          variant="standard"
          fullWidth
        >
          <MenuItem value="">All</MenuItem>
          {CHANGE_TYPE_OPTIONS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
        <TextField
          select
          label="Schema"
          value={schema}
          onChange={this.handleFilterChange('schema')}
          variant="standard"
          fullWidth
        >
          <MenuItem value="">All</MenuItem>
          {Array.isArray(schemas) && schemas.map(s => (
            <MenuItem key={s.schemaId} value={s.schemaId}>{s.instanceName} ({s.schemaName})</MenuItem>
          ))}
        </TextField>
        <TextField
          label="Changed By"
          value={changedBy}
          onChange={this.handleFilterChange('changedBy')}
          variant="standard"
          fullWidth
        />
        <TextField
          label="Search"
          value={search}
          onChange={this.handleFilterChange('search')}
          variant="standard"
          fullWidth
          InputProps={{
            endAdornment: search && (
              <InputAdornment position="end">
                <i className="material-icons search-clear" onClick={() => this.setState({ search: '' }, this.loadReport)}>close</i>
              </InputAdornment>
            )
          }}
        />
      </div>
    );
  }

  renderTable(data) {
    return (
      <div className="object-table-container">
        <table className="object-table">
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Schema</th>
              <th>Table</th>
              <th>Change Type</th>
              <th>Column</th>
              <th>Old → New</th>
              <th>Changed By</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, idx) => (
              <tr key={idx}>
                <td>{row.timestamp}</td>
                <td>{row.schema}</td>
                <td>{row.table}</td>
                <td>
                  <span className={`dd-badge badge-${(row.changeType || '').toLowerCase()}`}>
                    {row.changeType}
                  </span>
                </td>
                <td>{row.column}</td>
                <td className="dd-change-diff">
                  {/* Slice 2k: the fetcher redacts oldValue/newValue, so both arrive as the
                      '[redacted]' sentinel. One chip for the pair — they are the before/after of
                      the same withheld definition. */}
                  {(row.redactedFields || []).length > 0 ? (
                    <RedactedChip fields={row.redactedFields} data-testid="dd-audit-redacted" />
                  ) : (
                    <React.Fragment>
                      {row.oldValue && <span className="dd-old-val">{row.oldValue}</span>}
                      {row.oldValue && row.newValue && <span className="dd-diff-arrow"> → </span>}
                      {row.newValue && <span className="dd-new-val">{row.newValue}</span>}
                      {!row.oldValue && !row.newValue && <span className="dd-no-value">—</span>}
                    </React.Fragment>
                  )}
                </td>
                <td>{row.changedBy}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  render() {
    const { schemaAuditReport, onBack } = this.props;
    const data = schemaAuditReport.data || [];

    return (
      <ReportShell
        title="Schema Change / Audit Report"
        icon="history"
        summaryTiles={this.getSummaryTiles(data)}
        filters={this.renderFilters()}
        table={this.renderTable(data)}
        isLoading={schemaAuditReport.isLoading}
        error={schemaAuditReport.error}
        data={data}
        onBack={onBack}
        onDownload={this.handleDownload}
      />
    );
  }
}

const mapStateToProps = (state) => ({
  schemaAuditReport: state.dataDictionaryViewReducer.reports.schemaAudit,
  schemas: state.dataDictionaryViewReducer.schemas,
});

export default connect(mapStateToProps, { loadSchemaAuditReport, downloadReport })(SchemaAuditReport);
